<script lang="ts">
    import { goto } from "$app/navigation"
    import {
        SearchResult,
        getTopLevelClass,
        type RenderItem,
        type RenderSlippable,
        type Renderable,
        type WeighProcessable,
        FootnoteClass,
    } from "@circleofa/acim-engine"
    import Block from "@components/home/Block.svelte"
    import FocusMask from "@components/material/FocusMask.svelte"
    import Icon from "@components/material/Icon.svelte"
    import ProgressCircle from "@components/material/ProgressCircle.svelte"
    import Pill from "@components/utilities/Pill.svelte"
    import ContextMenu from "@components/utilities/context-menu/ContextMenu.svelte"
    import ContextMenuIcon from "@components/utilities/context-menu/ContextMenuIcon.svelte"
    import ContextMenuLink from "@components/utilities/context-menu/ContextMenuLink.svelte"
    import { styleFontSize } from "@store/deviceSettings"
    import { selectedTab, selectTab } from "@store/pageTracker"
    import { clickEnter } from "@utils/directives/accessibility"
    import {
        FocusExpander,
        SquintEventEmitter,
        focusserCompressorClass,
        focusserMoverClass,
    } from "@utils/highlights/directive-logic/focusser"
    import {
        ContextMenuViewportSelection,
        FocusSetting,
        HighlightSettingLimiter,
    } from "@utils/highlights/directive-logic/highlight-listener-permissions"
    import {
        RedrawEventDispatcher,
        attachHighlighterListener,
    } from "@utils/highlights/directive-logic/highlights-directive"
    import type { ReadyTracker } from "@utils/highlights/directive-logic/ready-tracker"
    import { removeUnboldedSentences } from "@utils/search/boldify-transformer"
    import { altKeyDown } from "@utils/underhood/registerHotkey"
    import { createEventDispatcher } from "svelte"
    import { twMerge } from "tailwind-merge"
    import { setKeyedPagePopWords } from "../book/[notation]/KeyedPage.svelte"
    import PopOut from "@components/PopOut.svelte"
    import { htmlRenderItems } from "@utils/reading/render"
    import { getSearchResultDisplayPath } from "./search-result-display"

    export let index: number
    export let className: string
    export let visited: boolean
    export let result: SearchResult
    export let symbol: RenderItem | ((Renderable | RenderSlippable) & WeighProcessable) | null
    export let renderItem: RenderItem | null
    export let redrawEventDispatcher: RedrawEventDispatcher
    export let forceInstantFirstPaint: boolean
    export let viewportSelection: ContextMenuViewportSelection
    export let disableSelection: boolean
    export let focusOn: FocusSetting | null
    export let squintEventEmitter: SquintEventEmitter
    export let readyTracker: ReadyTracker<SearchResult>
    export let directOpen = false

    const focusExpander = new FocusExpander()

    let loading = true
    let htmlFocus = false
    let selectionFocus = false

    $: actualFocus = htmlFocus || selectionFocus
    $: focusExpander.dispatch(actualFocus ? "expand" : "contract")

    let showContextMenu = false

    if (!renderItem) {
        loading = false
        readyTracker.set(result, true)
    }

    $: displayPath = getSearchResultDisplayPath(result)
</script>

<div class={loading ? "opacity-0 transition-opacity" : "opacity-100 transition-opacity"}>
    <Block
        class={twMerge(
            `relative focus:outline-2 gap-2 hover:bg-neutral-25 dark:hover:bg-neutral-900 ${
                !$altKeyDown && false ? "cursor-pointer" : ""
            }`,
            className,
            actualFocus ? "outline-2" : "",
        )}
        tabindex="0"
        data-visited={visited}
        aria-label={`go to ${displayPath}`}
        on:keydown={clickEnter}
        on:click
        on:focus={() => (htmlFocus = true)}
        on:blur={() => (htmlFocus = false)}
    >
        <PopOut positions={["bl", "tl"]} {directOpen} on:go bind:showContextMenu />
        {#if symbol}
            <div class="flex w-full justify-between items-center">
                <div>
                    <Pill category={getTopLevelClass(symbol)} />
                    <span class="font-bold">{displayPath}</span>
                    {#if result.target instanceof FootnoteClass}
                        Footnote #{result.target.number}
                    {/if}
                </div>
                <ProgressCircle percentage={result.percentRelevance} class="w-6 h-6 mr-8" />
            </div>
        {/if}
        <div use:styleFontSize>
            {#if result.showContent}
                {#if renderItem}
                    <div class="relative {focusserCompressorClass} overflow-hidden overscroll-auto">
                        <FocusMask />
                        <div class="relative {focusserMoverClass}">
                            <div
                                class="reader minireader z-1 px-2"
                                use:attachHighlighterListener={{
                                    redrawEventDispatcher,
                                    // disabled: disableSelection,
                                    settings: {
                                        forceInstantFirstPaint,
                                        popWords: result.wordRanges,
                                        emphasizeHighlight: "faded",
                                        createHighlights: false,
                                        editHighlights: HighlightSettingLimiter.NONE,
                                        viewportSelection,
                                        squintEventEmitter,
                                        focusOn,
                                        focusExpander,
                                        callbacks: {
                                            loading: () => {
                                                readyTracker.set(result, false)
                                                loading = true
                                            },
                                            painted: () => {
                                                readyTracker.set(result, true)
                                                loading = false
                                            },
                                            selected: () => {
                                                selectionFocus = true
                                            },
                                            deselected: () => {
                                                selectionFocus = false
                                            },
                                        },
                                    },
                                }}
                            >
                                {@html htmlRenderItems([renderItem])}
                            </div>
                        </div>
                    </div>
                {:else}
                    <p>
                        {@html removeUnboldedSentences(result.boldified)}
                    </p>
                {/if}
            {:else}
                <p>{result.niceLabel}</p>
            {/if}
        </div>
    </Block>
</div>
